import Cookies from "js-cookie";
import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";
import { MODULES, selectAppModule } from "../redux/appModule/appModuleSlice.js";

// The admin surfaces (/MeetingRooms, /user-bookings, /admin-dashboard,
// /user-management, …) are shared across modules — the same route renders CoWork,
// Makers Lab, Eduspace or Bloomberg data depending on the selected module. So the
// guard has to be module-aware: being an admin of one module must not unlock the
// admin routes while a *different* module is selected.
//
// Roles are namespaced by module ("Cowork Lab *" → Makers Lab, "Cowork Class *" →
// Eduspace, "BMB *" → Bloomberg, plain "Cowork *" → CoWork), so each module maps
// to the role(s) that make a user its admin.
const MODULE_ADMIN_ROLES = {
  [MODULES.COWORK]: ["Cowork Admin"],
  [MODULES.MAKERSLAB]: ["Cowork Lab Admin"],
  [MODULES.EDUSPACE]: ["Cowork Class Admin"],
  [MODULES.BLOOMBERG]: ["BMB Admin"],
};

// True when the user holds an admin role for `moduleKey`. Exported so pages can
// reuse the same rule instead of re-deriving it from role strings.
export const isModuleAdmin = (roles = [], moduleKey) => {
  const allowed = MODULE_ADMIN_ROLES[moduleKey] || [];
  return (Array.isArray(roles) ? roles : []).some((role) => allowed.includes(role));
};

export const getRolesFromCookie = () => {
  const rolesCookie = Cookies.get("roles");
  try {
    const parsed = rolesCookie ? JSON.parse(rolesCookie) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

// Route guard for admin-only pages. Sits inside PrivateRoute + MainLayout, so the
// user is already authenticated and the app chrome is rendered; this only gates
// the admin routes. A user who is an admin of the selected module passes through;
// anyone else (including admins of *other* modules) is bounced back to the
// original route, which resolves their own dashboard via DynamicDashboard.
export const AdminRoute = () => {
  const selectedModule = useSelector(selectAppModule);
  const roles = getRolesFromCookie();

  return isModuleAdmin(roles, selectedModule) ? <Outlet /> : <Navigate to="/" replace />;
};
